import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useSiteSelector } from "@/hooks/useSiteSelector";
import { useAuth } from "@/hooks/useAuth";
import {
  COMPANY_SIDEBAR_NAV,
  SITE_MANAGER_SIDEBAR_NAV,
} from "@/config/navigation";
import { Bolt, Plus, Truck } from "lucide-react";

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { sites, selectSite } = useSiteSelector();

  const isSiteManager = user?.role === "site_manager";

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "k") {
      e.preventDefault();
      setOpen((prev) => !prev);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const runCommand = (command: () => void) => {
    setOpen(false);
    command();
  };

  const navItems = isSiteManager
    ? SITE_MANAGER_SIDEBAR_NAV
    : COMPANY_SIDEBAR_NAV.filter((item) => {
        if (item.restrictedTo) return item.restrictedTo.includes(user?.role || "");
        return true;
      });

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Rechercher un chantier, une page, une action..." />
      <CommandList>
        <CommandEmpty>Aucun résultat.</CommandEmpty>

        {sites.length > 0 && (
          <CommandGroup heading="Chantiers">
            {sites.map((site) => (
              <CommandItem
                key={site.id}
                value={site.name}
                onSelect={() =>
                  runCommand(() => {
                    selectSite(site);
                    if (isSiteManager) {
                      navigate("/site");
                    } else {
                      navigate(`/sites/${site.short_code ?? site.id}`);
                    }
                  })
                }
              >
                <Bolt className="mr-2 h-4 w-4" />
                <span>{site.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandSeparator />

        <CommandGroup heading="Navigation">
          {navItems.map((item) => (
            <CommandItem
              key={item.url}
              value={item.title}
              onSelect={() => runCommand(() => navigate(item.url))}
            >
              <item.icon className="mr-2 h-4 w-4" />
              <span>{item.title}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Actions">
          {isSiteManager ? (
            <>
              <CommandItem
                value="Nouveau bétonnage"
                onSelect={() => runCommand(() => navigate("/site?nouveau=true"))}
              >
                <Plus className="mr-2 h-4 w-4" />
                <span>Nouveau bétonnage</span>
              </CommandItem>
              <CommandItem
                value="Réserver des camions"
                onSelect={() => runCommand(() => navigate("/site?reservation=true"))}
              >
                <Truck className="mr-2 h-4 w-4" />
                <span>Réserver des camions</span>
              </CommandItem>
            </>
          ) : (
            <CommandItem
              value="Nouveau chantier"
              onSelect={() => runCommand(() => navigate("/sites?create=true"))}
            >
              <Plus className="mr-2 h-4 w-4" />
              <span>Nouveau chantier</span>
            </CommandItem>
          )}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
